import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useItemListContext } from './ItemListProvider';
import { Modal } from '@/components/ui/Modal';
import type { NormalizedItem } from './types';
import RenameModal from '../modals/renameModal/renameModal';
import DeleteItemModal from '../modals/deleteItemModal/DeleteItemModal';
import CopyItemModal from '../modals/copyItemModal/CopyItemModal';
import MoveItemModal from '../modals/moveItemModal/MoveItemModal';
import ShareFileModal from '../modals/shareFileModal/ShareFileModal';
import { ConvertModal } from '../modals/convertModal/ConvertModal';
import FileUploadingModal from '../modals/fileUploadingModal/FileUploadingModal';
import NewItemModal from '../modals/newItemModal/NewItemModal';
import SetPictureModal from '../modals/setPictureModal/SetPictureModal';

export function ItemListModals() {
  const {
    modal,
    closeModal,
    selectedItemsArray,
    items: contextItems,
    onRefresh,
    updateIsQuerying,
    updateItem,
    removeItems,
  } = useItemListContext();
  const { id: routeId, parentId: routeParentId } = useParams<{ id: string; parentId: string }>();
  // parentId is either the folder ID from the route, or the project ID
  const uploadParentId = routeId || routeParentId || 'root';
  const existingResourceNames = contextItems
    .filter((i) => i.itemtype === 'resource')
    .map((i) => i.name || i.title);
  const existingItemNames = contextItems
    .filter((i) => i.itemtype === 'folder' || i.itemtype === 'project')
    .map((i) => i.title || i.name || '');

  // Optimistic update for rename: update item title locally, no full refetch
  const handleRenamed = useCallback((id: string, newTitle: string) => {
    updateItem(id, { title: newTitle, name: newTitle });
  }, [updateItem]);

  const handleRenameRefresh = useCallback(() => {
    // no-op — the optimistic handleRenamed callback handles the UI update
  }, []);

  // Optimistic refresh for delete: remove items locally, no full refetch
  const handleDeleteRefresh = useCallback(() => {
    const ids = (modal?.params as Record<string, unknown>)?.items as NormalizedItem[] | undefined;
    if (ids) {
      removeItems(ids.map((i) => i.id));
    }
  }, [modal, removeItems]);

  // Optimistic refresh for move: remove moved items from current view
  const handleMoveRefresh = useCallback(() => {
    const items = (modal?.params as Record<string, unknown>)?.items as NormalizedItem[] | undefined;
    if (items) {
      removeItems(items.map((i) => i.id));
    }
  }, [modal, removeItems]);

  if (!modal) return null;

  const params = (modal.params || {}) as Record<string, unknown>;
  const items = (params.items || selectedItemsArray) as unknown[];
  const item = params.item as Record<string, unknown> | undefined;

  const modalTitle = getModalTitle(modal.type);
  const modalSize = getModalSize(modal.type);

  return (
    <Modal open={!!modal} onClose={closeModal} title={modalTitle} size={modalSize}>
      {modal.type === 'rename' && item && (
        <RenameModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          item={item}
          title="Rename"
          placeholder="New name"
          refreshList={handleRenameRefresh}
          onRenamed={handleRenamed}
        />
      )}
      {modal.type === 'delete' && (
        <DeleteItemModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          selectedItems={items}
          refreshList={handleDeleteRefresh}
          userIsAdmin={true}
        />
      )}
      {modal.type === 'copy' && (
        <CopyItemModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          selectedItems={items}
          removeFromSelectedItems={() => {}}
          refreshList={onRefresh}
          match={{ params: {} }}
          pathname=""
          workspaceName=""
        />
      )}
      {modal.type === 'move' && (
        <MoveItemModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          selectedItems={items}
          removeFromSelectedItems={() => {}}
          refreshList={handleMoveRefresh}
          match={{ params: {} }}
          pathname=""
          workspaceName=""
          history={{ push: () => {} }}
        />
      )}
      {modal.type === 'shareFile' && (
        <ShareFileModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          selectedFiles={items}
        />
      )}
      {modal.type === 'convert' && item && (
        <ConvertModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          item={item}
          refreshList={onRefresh}
        />
      )}
      {modal.type === 'fileUpload' && (
        <FileUploadingModal
          closeModal={closeModal}
          onRefresh={onRefresh}
          parentId={uploadParentId}
          existingNames={existingResourceNames}
          useCase="folder"
        />
      )}
      {modal.type === 'newItem' && (
        <NewItemModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          refreshList={onRefresh}
          existingNames={existingItemNames}
        />
      )}
      {modal.type === 'coverImage' && item && (
        <SetPictureModal
          closeModal={closeModal}
          updateIsQuerying={updateIsQuerying}
          item={item}
          useCase="coverImage"
          refreshList={onRefresh}
        />
      )}
    </Modal>
  );
}

function getModalTitle(type: string): string {
  switch (type) {
    case 'rename': return 'Rename';
    case 'delete': return 'Delete';
    case 'copy': return 'Copy items';
    case 'move': return 'Move items';
    case 'shareFile': return 'Share';
    case 'convert': return 'Convert';
    case 'fileUpload': return 'Upload files';
    case 'newItem': return 'New item';
    case 'coverImage': return 'Cover image';
    default: return '';
  }
}

function getModalSize(type: string): 'sm' | 'md' | 'lg' {
  switch (type) {
    case 'fileUpload':
    case 'coverImage':
      return 'lg';
    case 'copy':
    case 'move':
      return 'md';
    default:
      return 'sm';
  }
}
